import React, { useEffect, useState, useCallback } from 'react';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import EntrySheet from '../components/EntrySheet';

const EMPTY_FORM = {
  product_id:   '',
  product_name: '',
  product_type: 'finished',
  qty:          '',
  unit_cost:    '',
  supplier:     '',
  purchased_at: '',
};

const fmt = (n) =>
  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
import { fmtDate } from '../utils/date';
import { unitLabel } from '../utils/unit';

export default function CompanyPurchases() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [businesses, setBusinesses]     = useState([]);
  const [selectedBiz, setSelectedBiz]   = useState(null);
  const [bizData, setBizData]           = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [useNewProduct, setUseNewProduct] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [editEntry, setEditEntry]       = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [editSaving, setEditSaving]     = useState(false);
  const [editError, setEditError]       = useState('');

  useEffect(() => {
    companyAPI.listBiz().then(r => setBusinesses(r.data.businesses || []));
  }, []);

  const loadBiz = useCallback(async (biz) => {
    setSelectedBiz(biz);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setError('');
    setLoading(true);
    try {
      const r = await companyAPI.getBusinessDetail(biz.id);
      setBizData(r.data);
    } catch {
      setBizData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedBiz) { setError('Select a business first'); return; }
    if (!useNewProduct && !form.product_id) { setError('Select a product or switch to new product'); return; }
    if (useNewProduct && !form.product_name.trim()) { setError('Enter a product name'); return; }
    if (!form.qty || parseFloat(form.qty) <= 0) { setError('Enter a valid quantity'); return; }

    setSaving(true);
    try {
      const payload = {
        qty:          parseFloat(form.qty),
        unit_cost:    parseFloat(form.unit_cost) || 0,
        supplier:     form.supplier || undefined,
        purchased_at: form.purchased_at ? new Date(form.purchased_at).toISOString() : undefined,
        product_type: form.product_type,
      };
      if (useNewProduct) {
        payload.product_name = form.product_name.trim();
      } else {
        payload.product_id = form.product_id;
      }
      await companyAPI.addBizPurchase(selectedBiz.id, payload);
      setForm(EMPTY_FORM);
      setUseNewProduct(false);
      setShowForm(false);
      await loadBiz(selectedBiz);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record purchase');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (p) => {
    setEditEntry(p);
    setEditForm({
      qty:          String(parseFloat(Number(p.qty).toFixed(3))),
      unit_cost:    String(parseFloat(Number(p.unit_cost || 0).toFixed(2))),
      supplier:     p.supplier || '',
      purchased_at: p.purchased_at ? new Date(p.purchased_at).toISOString().slice(0, 16) : '',
    });
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditError('');
    if (!editForm.qty || parseFloat(editForm.qty) <= 0) { setEditError('Enter a valid quantity'); return; }
    setEditSaving(true);
    try {
      await companyAPI.updateBizPurchase(selectedBiz.id, editEntry.id, {
        qty:          parseFloat(editForm.qty),
        unit_cost:    parseFloat(editForm.unit_cost) || 0,
        supplier:     editForm.supplier || undefined,
        purchased_at: editForm.purchased_at ? new Date(editForm.purchased_at).toISOString() : undefined,
      });
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditDelete = async () => {
    setEditSaving(true);
    try {
      await companyAPI.voidBizPurchase(selectedBiz.id, editEntry.id);
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setEditSaving(false);
    }
  };

  const products   = bizData?.products   || [];
  const purchases  = bizData?.purchases  || [];
  const currency   = selectedBiz?.currency || '';

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <BackButton />
      <h1 style={{ margin: '0 0 1.2rem', fontSize: '1.4rem', fontWeight: 700 }}>Company Purchases</h1>

      {/* Business selector */}
      <div style={{ marginBottom: '1.2rem' }}>
        <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: 4 }}>
          Select business
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {businesses.map(b => (
            <button
              key={b.id}
              onClick={() => loadBiz(b)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 20, border: '1px solid var(--color-border)',
                background: selectedBiz?.id === b.id ? 'var(--color-primary)' : 'transparent',
                color: selectedBiz?.id === b.id ? '#fff' : 'inherit',
                cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500,
              }}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {selectedBiz && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }}>{selectedBiz.name} · {currency}</span>
            <button
              onClick={() => { setShowForm(s => !s); setError(''); }}
              style={{
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '0.45rem 1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              {showForm ? 'Cancel' : '+ Add Purchase'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} style={{
              background: 'var(--color-card)', borderRadius: 12, padding: '1.2rem',
              marginBottom: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
            }}>
              {/* Product toggle */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {['Existing product', 'New product'].map((label, i) => (
                  <button
                    key={label} type="button"
                    onClick={() => { setUseNewProduct(i === 1); setForm(f => ({ ...f, product_id: '', product_name: '' })); }}
                    style={{
                      flex: 1, padding: '0.4rem', borderRadius: 8,
                      border: '1px solid var(--color-border)',
                      background: useNewProduct === (i === 1) ? 'var(--color-primary)' : 'transparent',
                      color: useNewProduct === (i === 1) ? '#fff' : 'inherit',
                      cursor: 'pointer', fontSize: '0.85rem',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {!useNewProduct ? (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Product</span>
                  <select
                    required
                    value={form.product_id}
                    onChange={e => {
                      const p = products.find(p => p.id === e.target.value);
                      setForm(f => ({ ...f, product_id: e.target.value, product_type: p?.product_type || 'finished' }));
                    }}
                    style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                  >
                    <option value="">— select —</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} · stock: {parseFloat(Number(p.stock_qty).toFixed(3))}{unitLabel(p.unit) ? ` ${unitLabel(p.unit)}` : ''}
                        {p.product_type === 'raw_material' ? ' · raw' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>New product name</span>
                  <input
                    type="text" required value={form.product_name}
                    onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                    placeholder="e.g. Empty Bottle 500ml"
                    style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                  />
                </label>
              )}

              {/* Product type */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Product type</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { value: 'finished',     label: '✅ Finished product', desc: 'Ready to sell' },
                    { value: 'raw_material', label: '⚙️ Raw material',     desc: 'Used in production' },
                  ].map(opt => (
                    <button
                      key={opt.value} type="button"
                      onClick={() => setForm(f => ({ ...f, product_type: opt.value }))}
                      style={{
                        flex: 1, padding: '0.5rem 0.4rem', borderRadius: 8, textAlign: 'center',
                        border: '1px solid var(--color-border)',
                        background: form.product_type === opt.value ? 'var(--color-primary)' : 'transparent',
                        color: form.product_type === opt.value ? '#fff' : 'inherit',
                        cursor: 'pointer', fontSize: '0.82rem',
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: '0.74rem', opacity: 0.8 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Qty</span>
                  <input
                    type="number" min="0.001" step="any" required value={form.qty}
                    onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
                    placeholder="e.g. 100"
                    style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Unit cost ({currency})</span>
                  <input
                    type="number" min="0" step="any" value={form.unit_cost}
                    onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))}
                    placeholder="0.00"
                    style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                  />
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Supplier <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="text" value={form.supplier}
                  onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                  placeholder="e.g. Bottle Co., Own Hives"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Date <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="datetime-local" value={form.purchased_at}
                  onChange={e => setForm(f => ({ ...f, purchased_at: e.target.value }))}
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.88rem' }}>{error}</div>}

              <button
                type="submit" disabled={saving}
                style={{
                  background: 'var(--color-primary)', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '0.65rem', fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Record Purchase'}
              </button>
            </form>
          )}

          {/* Purchases list */}
          {loading ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>Loading…</div>
          ) : purchases.length === 0 ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>
              No purchases yet for {selectedBiz.name}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {purchases.map(p => (
                <div key={p.id} onClick={isOwner ? () => openEdit(p) : undefined} style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: '0.85rem 1rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: isOwner ? 'pointer' : 'default',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{p.product_name}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                      Qty: {parseFloat(Number(p.qty).toFixed(3))}
                      {p.unit_cost > 0 ? ` · Cost/unit: ${currency} ${fmt(p.unit_cost)}` : ''}
                      {p.supplier ? ` · ${p.supplier}` : ''}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>{fmtDate(p.purchased_at)}</div>
                  </div>
                  {p.total_cost > 0 && (
                    <div style={{ fontWeight: 700, color: 'var(--color-danger)', whiteSpace: 'nowrap' }}>
                      {currency} {fmt(p.total_cost)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {isOwner && <EntrySheet
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        title={editEntry ? `Edit: ${editEntry.product_name}` : ''}
        fields={[
          { key: 'qty',          label: 'Qty',                type: 'number', required: true },
          { key: 'unit_cost',    label: `Unit cost (${currency})`, type: 'number' },
          { key: 'supplier',     label: 'Supplier' },
          { key: 'purchased_at', label: 'Date', type: 'datetime-local' },
        ]}
        values={editForm}
        onChange={(k, v) => setEditForm(f => ({ ...f, [k]: v }))}
        onSave={handleEditSave}
        onDelete={handleEditDelete}
        saving={editSaving}
        error={editError}
      />}
    </div>
  );
}
